import { useIntl } from 'react-intl';

import { ContentContainer } from '~/eds/src/components/ContentContainer/ContentContainer';
import { Layout } from '~/eds/src/components/Layout/Layout';
import { StatusText } from '~/eds/src/components/StatusText/StatusText';

import { LibraryModelVersion } from '../types';

interface Props {
  isDraft: boolean;
  models: LibraryModelVersion[];
}

export const ModelPublishStates = ({ isDraft, models }: Props) => {
  const intl = useIntl();

  const displayStates = models.map((model) => {
    if (model) {
      const { label, publishErrorMessage, publishState, testCaseState } = model;
      switch (publishState) {
        case 'done':
          if (testCaseState === 'error') {
            return (
              <StatusText
                key={model.libraryModelId}
                icon="status-warning"
                iconColor="status.warning"
                iconSpacing="m"
                text={label}
                tooltip={
                  isDraft
                    ? intl.formatMessage({
                        id: 'ModelPublishStates.savedWithTestCaseError',
                        defaultMessage:
                          'The model was successfully saved, but there was an error initializing the test case.',
                        description:
                          'Tooltip when model saves successfully but test case initialization fails',
                      })
                    : intl.formatMessage({
                        id: 'ModelPublishStates.publishedWithTestCaseError',
                        defaultMessage:
                          'The model was successfully published, but there was an error initializing the test case.',
                        description:
                          'Tooltip when model publishes successfully but test case initialization fails',
                      })
                }
              />
            );
          }
          if (testCaseState === 'pending') {
            return (
              <StatusText
                key={model.libraryModelId}
                icon="loading"
                iconColor="status.info"
                iconSpacing="m"
                text={label}
              />
            );
          }
          return (
            <StatusText
              key={model.libraryModelId}
              icon="status-success"
              iconColor="status.success"
              iconSpacing="m"
              text={label}
            />
          );
        case 'error':
          return (
            <StatusText
              key={model.libraryModelId}
              icon="status-reject"
              iconColor="status.danger"
              iconSpacing="m"
              text={label}
              tooltip={publishErrorMessage}
            />
          );
        case 'pending':
        default:
          return (
            <StatusText
              key={model.libraryModelId}
              icon="loading"
              iconColor="status.info"
              iconSpacing="m"
              text={label}
            />
          );
      }
    }
    return null;
  });

  return (
    <ContentContainer
      loadingContent={{ isLoading: displayStates.length === 0 }}
    >
      <Layout direction="column" spacing={2}>
        {displayStates}
      </Layout>
    </ContentContainer>
  );
};
